<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDocCategory } from '@/composables/useDocCategory'
import { useDocSearch } from '@/composables/useDocSearch'
import { useDocsContext } from '@/composables/useDocsContext'
import { useLocale } from '@/composables/useLocale'
import { DocCategoryValues } from '@/types/docs'
import type { DocCategory } from '@/types/docs'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import DocsPublicLayout from '@/components/layout/DocsPublicLayout.vue'
import DocsSearchPalette from '@/components/common/docs/DocsSearchPalette.vue'

const { t } = useI18n()
const route = useRoute()
const { base, isPublic } = useDocsContext()
const { currentLocale } = useLocale()
const { ensureLoaded } = useDocSearch()

const categoryParam = computed(() => route.params.category as string)

// Validate the category slug against the known set
const isValidCategory = computed<boolean>(() =>
  (DocCategoryValues as string[]).includes(categoryParam.value),
)

const { articles, loading, error, notFound, fetch } = useDocCategory()

const paletteOpen = ref(false)

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString(currentLocale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function handleGlobalKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    paletteOpen.value = true
  }
}

onMounted(() => {
  if (isValidCategory.value) {
    void fetch(categoryParam.value)
    void ensureLoaded(currentLocale.value)
  }
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})
</script>

<template>
  <component :is="isPublic ? DocsPublicLayout : OrganizerLayout">
    <main class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Unknown category: 404 -->
      <div
        v-if="!isValidCategory || notFound"
        class="text-center py-20"
      >
        <p class="text-slate-300 text-lg font-medium mb-2">
          {{ t('docs.article.not_found_title') }}
        </p>
        <p class="text-slate-400 text-sm mb-6">
          {{ t('docs.article.not_found_body') }}
        </p>
        <RouterLink
          :to="base"
          class="text-sm text-emerald-400 hover:underline"
        >
          {{ t('docs.article.tree_label') }}
        </RouterLink>
      </div>

      <template v-else>
        <!-- Breadcrumb -->
        <nav
          aria-label="breadcrumb"
          class="flex items-center gap-2 text-xs text-slate-500 mb-6"
        >
          <RouterLink
            :to="base"
            class="hover:text-slate-300 transition-colors"
          >
            {{ t('docs.breadcrumb.docs') }}
          </RouterLink>
          <span aria-hidden="true">/</span>
          <span class="text-slate-300">
            {{ t(`docs.category.${categoryParam}.title`) }}
          </span>
        </nav>

        <!-- Heading -->
        <div class="mb-6">
          <h1 class="text-2xl font-bold text-white mb-1">
            {{ t(`docs.category.${categoryParam}.title`) }}
          </h1>
          <p class="text-sm text-slate-400">
            {{ t(`docs.category.${categoryParam}.blurb`) }}
          </p>
        </div>

        <!-- Search affordance -->
        <div class="mb-8">
          <button
            type="button"
            class="flex items-center gap-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-slate-500 px-3 py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors"
            @click="paletteOpen = true"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {{ t('docs.search.placeholder') }}
          </button>
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400 mb-6"
        >
          {{ t('docs.category.error') }}
        </div>

        <!-- Skeleton -->
        <div
          v-if="loading"
          class="space-y-3"
          aria-busy="true"
        >
          <div
            v-for="i in 5"
            :key="i"
            class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 animate-pulse"
          >
            <div class="h-4 bg-slate-700 rounded w-1/2 mb-2" />
            <div class="h-3 bg-slate-700 rounded w-3/4 mb-1" />
            <div class="h-3 bg-slate-700 rounded w-1/4" />
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!loading && articles.length === 0 && !error"
          class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-12 text-center"
        >
          <p class="text-slate-300 text-sm font-medium mb-1">
            {{ t('docs.category.empty_title') }}
          </p>
          <p class="text-slate-400 text-sm">
            {{ t('docs.category.empty_body') }}
          </p>
        </div>

        <!-- Article list -->
        <div
          v-else-if="!loading"
          class="space-y-3"
        >
          <RouterLink
            v-for="article in articles"
            :key="article.slug"
            :to="`${base}/${categoryParam}/${article.slug}`"
            class="block rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-800 hover:border-slate-600 px-5 py-4 transition-all group"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <h2 class="text-sm font-semibold text-white group-hover:text-emerald-400 transition-colors mb-1">
                  {{ article.title }}
                </h2>
                <p
                  v-if="article.excerpt"
                  class="text-xs text-slate-400 line-clamp-2 mb-2"
                >
                  {{ article.excerpt }}
                </p>
                <p
                  v-if="article.last_reviewed_at"
                  class="text-xs text-slate-600"
                >
                  {{ t('docs.article.last_reviewed', { date: formatDate(article.last_reviewed_at) }) }}
                </p>
              </div>
              <!-- Outdated badge -->
              <span
                v-if="article.status === 'outdated'"
                class="shrink-0 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20"
              >
                {{ t('docs.article.outdated_banner') }}
              </span>
            </div>
          </RouterLink>
        </div>

        <!-- Back link -->
        <div class="mt-8">
          <RouterLink
            :to="base"
            class="text-sm text-slate-500 hover:text-slate-300 transition-colors"
          >
            ← {{ t('docs.category.heading_back') }}
          </RouterLink>
        </div>
      </template>
    </main>

    <!-- Search palette -->
    <DocsSearchPalette
      :open="paletteOpen"
      :base="base"
      @close="paletteOpen = false"
    />
  </component>
</template>
